/**
 * Support Copilot page-context capture (CLOSEOUT-ESTIMATE-INTEL §D4.4).
 *
 * When the Help drawer opens it needs to already know where the user is —
 * "I can see you're on <thing>" — without the user explaining. Two layers:
 *
 * 1. Route auto-parse: entity ids and page names derived from the current
 *    pathname + query string. Works everywhere with zero instrumentation.
 * 2. Structured snapshots: pages call `reportPageContext()` (or the
 *    `usePageContextReporter` hook) with key on-screen values — job number,
 *    customer name, active tab, applied filters. Last writer wins; cleared
 *    on unmount so stale values never leak across pages.
 */
import { useEffect } from "react";

export interface PageContextSnapshot {
    /** Human label for the thing on screen, e.g. `Job #072654690 — PetSmart`. */
    label?: string;
    entity?: { type: string; id: string };
    activeTab?: string;
    filters?: Record<string, unknown>;
    values?: Record<string, unknown>;
}

let currentSnapshot: PageContextSnapshot | null = null;

export function reportPageContext(snapshot: PageContextSnapshot | null): void {
    currentSnapshot = snapshot;
}

/** Report a structured snapshot for the lifetime of the calling component. */
export function usePageContextReporter(snapshot: PageContextSnapshot | null): void {
    const serialized = JSON.stringify(snapshot ?? null);
    useEffect(() => {
        currentSnapshot = snapshot;
        return () => {
            currentSnapshot = null;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [serialized]);
}

/** Route patterns → entity descriptors. Order matters (first match wins). */
const ROUTE_PATTERNS: Array<{ re: RegExp; page: string; entityType?: string }> = [
    {
        re: /^\/jobs\/work-orders\/review\/([0-9a-f-]{36})/i,
        page: "Work Order Intake Review",
        entityType: "work_order_intake_item",
    },
    { re: /^\/jobs\/([0-9a-f-]{36})/i, page: "Job Detail", entityType: "jobs" },
    { re: /^\/commercial\/([0-9a-f-]{36})/i, page: "Estimate / Invoice Detail", entityType: "estimates" },
    { re: /^\/invoices\/([0-9a-f-]{36})/i, page: "Invoice Detail", entityType: "invoices" },
    { re: /^\/customers\/([0-9a-f-]{36})/i, page: "Customer Detail", entityType: "customers" },
    { re: /^\/sites\/([0-9a-f-]{36})/i, page: "Site Detail", entityType: "sites" },
    { re: /^\/assets\/([0-9a-f-]{36})/i, page: "Device Detail", entityType: "assets" },
    { re: /^\/purchasing\/([0-9a-f-]{36})/i, page: "Purchase Order", entityType: "purchase_orders" },
    { re: /^\/office-review/i, page: "Close Out (Office Review)" },
    // #2178: these used to all read "Groundwork", so the Help Desk didn't
    // know someone asking about tax was in the Mailroom.
    { re: /^\/close-out/i, page: "Close Out" },
    { re: /^\/mailroom/i, page: "Mailroom" },
    { re: /^\/booking/i, page: "Bookings" },
    { re: /^\/customers/i, page: "Customers" },
    { re: /^\/sites/i, page: "Locations" },
    { re: /^\/assets/i, page: "Devices" },
    { re: /^\/pricebook/i, page: "Fieldbook (Pricebook)" },
    { re: /^\/tasks/i, page: "Tasks" },
    { re: /^\/command/i, page: "Command Center" },
    { re: /^\/payroll/i, page: "Payroll" },
    { re: /^\/compliance/i, page: "Compliance" },
    { re: /^\/settings/i, page: "Settings" },
    { re: /^\/dispatch/i, page: "The Grid (Dispatch)" },
    { re: /^\/inbox/i, page: "Work Inbox" },
    { re: /^\/money/i, page: "Money Page" },
    { re: /^\/purchasing/i, page: "Purchasing" },
    { re: /^\/support\/findings/i, page: "Dev Findings Board" },
    { re: /^\/admin\/system-health/i, page: "System Health" },
    { re: /^\/admin/i, page: "Admin" },
];

export interface CapturedPageContext {
    route: string;
    page: string;
    entity: { type: string; id: string } | null;
    query: Record<string, string>;
    snapshot: PageContextSnapshot | null;
}

/** Capture everything known about the current page. Call at drawer open. */
export function capturePageContext(loc: { pathname: string; search: string } = window.location): CapturedPageContext {
    const pathname = loc.pathname;
    let page = "Groundwork";
    let entity: { type: string; id: string } | null = null;
    for (const p of ROUTE_PATTERNS) {
        const m = p.re.exec(pathname);
        if (m) {
            page = p.page;
            if (p.entityType && m[1]) entity = { type: p.entityType, id: m[1] };
            break;
        }
    }
    const query: Record<string, string> = {};
    for (const [k, v] of new URLSearchParams(loc.search)) query[k] = v;
    // office-review?job=<uuid> carries its entity in the query string.
    if (!entity && typeof query.job === "string" && /^[0-9a-f-]{36}$/i.test(query.job)) {
        entity = { type: "jobs", id: query.job };
    }
    const snapshot = currentSnapshot;
    if (snapshot?.entity) entity = snapshot.entity;
    return { route: pathname + loc.search, page, entity, query, snapshot };
}

/** One human sentence for the drawer's opener. */
export function describePageContext(ctx: CapturedPageContext): string {
    if (ctx.snapshot?.label) return ctx.snapshot.label;
    // #1701 — never an internal id in a sentence a person reads ("Groundwork
    // (jobs cbf08bf5)" in the 2026-10-01 test drive). The model still gets the
    // id in its page context; people get the page's name.
    return ctx.page;
}
